import { RemitStripePayoutLink } from '@api/payout';
import { createStripeConnectCustomAccount, checkStripeConnectCustomUser } from '@api/user';
import { isMerchantOfRecordCountry } from '@utils/helpers';
import faceIcon from '@assets/images/payout_page/Details.png';
import claimtip from '@assets/images/payout_page/Transfer.svg';
import { CircularProgress } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import StripeAccountOnboarding from '../../TipDirectPages/Balance/StripeConnectPortal/Onboarding';

export default function StripeInfoCollect({ user }) {
  let [errorText, setErrorText] = useState(false);

  const [stripeAccount, setStripeAccount] = useState(null);
  const [onboardingKey, setOnboardingKey] = useState(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [checkingMessage, setCheckingMessage] = useState('');

  const onOnboardingExit = async () => {
    setIsCheckingStatus(true);
    setCheckingMessage('Verifying details with Stripe..');

    const finalizeAndRedirect = async () => {
      try {
        await RemitStripePayoutLink();
      } catch (err) {
        console.error('Failed to remit balance, proceeding to redirect:', err);
      }
      window.location.href = '/tipdirect/balance';
    };

    let checkCount = 0;
    const maxChecks = 30;
    const intervalMs = 2000;

    const pollStatus = async () => {
      try {
        const response = await checkStripeConnectCustomUser();
        const status = response.data;

        // Check if there are any new requirements currently due or past due
        const hasRequirements =
          (status.requirements?.currently_due && status.requirements.currently_due.length > 0) ||
          (status.requirements?.past_due && status.requirements.past_due.length > 0);

        if (hasRequirements) {
          setCheckingMessage('Stripe requires additional verification. Reloading form...');
          setTimeout(() => {
            setOnboardingKey(prev => prev + 1);
            setIsCheckingStatus(false);
          }, 1500);
          return true; // Stop polling
        }

        // Check if capabilities are active
        const shouldBeMerchantOfRecord = isMerchantOfRecordCountry(user.profile?.country);
        const isFullyActive = shouldBeMerchantOfRecord
          ? (status.capabilities?.transfers === 'active' && status.capabilities?.card_payments === 'active')
          : (status.capabilities?.transfers === 'active');

        if (isFullyActive) {
          setCheckingMessage('Verification successful! Transferring tips...');
          await finalizeAndRedirect();
          return true; // Stop polling
        }

        // If not active, but no requirements, we increment checks and try again
        checkCount++;
        if (checkCount >= maxChecks) {
          setCheckingMessage('Onboarding processing. Redirecting...');
          await finalizeAndRedirect();
          return true;
        }

        // Continue polling
        setCheckingMessage('Verifying details with Stripe... (Please note: this can take up to 1 minute)');
        setTimeout(pollStatus, intervalMs);
      } catch (err) {
        console.error('Error checking Stripe status:', err);
        // Fallback to exit on error
        await finalizeAndRedirect();
        return true;
      }
    };

    // Start polling
    setTimeout(pollStatus, 500);
  };

  //Create stripe account if one doesnt exist (Server decision)
  useEffect(() => {
    async function fetchStripeAccount() {
      console.log('calling fetchStripeAccount')
      let accountInfo = await createStripeConnectCustomAccount();
      setStripeAccount(accountInfo.data);
    }
    fetchStripeAccount();
  }, []);

  //User performs stripe onboarding
  let pageContent = (
    <div className="mx-auto">
      <div className="mx-auto mb-3 text-center text-sm text-slate-600">
        Please wait while we create a stripe connect custom account
      </div>
      <div className="flex justify-center">
        <CircularProgress />
      </div>
    </div>
  );

  if (isCheckingStatus) {
    pageContent = (
      <div className="mx-auto text-center p-5">
        <div className="mb-4 font-bold text-slate-800">
          {checkingMessage}
        </div>
        <div className="flex justify-center">
          <CircularProgress style={{ color: '#6997FF' }} />
        </div>
      </div>
    );
  } else if (stripeAccount) {
    pageContent = (
      <div className="mx-auto w-[90%]">
        <StripeAccountOnboarding key={onboardingKey} onExit={onOnboardingExit} user={user} />
      </div>
    );
  }
  //Init outstanding transfer

  return (
    <div className="w-full px-4 pb-10">
      <div className="flex flex-col max-w-md mx-auto">
        <div className="w-20 h-20 rounded-full bg-white shadow-sm border border-solid border-slate-200 flex items-center justify-center overflow-hidden mx-auto mt-5 mb-6">
          <img src={faceIcon} className="w-[60px] h-[60px]" />
        </div>
        <div className="bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-solid border-slate-200 p-4 flex text-[13px] text-slate-600">
            <div className="w-9 h-9 rounded-full bg-white shadow-sm border border-solid border-slate-200 flex items-center justify-center overflow-hidden mr-3 shrink-0">
              <img src={claimtip} />
            </div>
            <div>
              <span className="font-semibold text-slate-800">Enter your details!</span> - We need to collect
              some additional info to process your payouts. You will only need to do this once.
            </div>
          </div>
        </div>
        {pageContent}
        <div
          className="bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6 mb-6"
          style={{ display: errorText.length > 0 ? 'block' : 'none' }}>
          <div className="bg-white rounded-2xl shadow-sm border border-solid border-red-200 p-4 flex text-[13px] text-red-600">
            <div>{errorText}</div>
          </div>
        </div>
      </div>
      <div className="text-center font-semibold text-xs text-slate-500 tracking-wide max-w-[240px] mx-auto mt-6">
        TipDirect payouts are made via secure end-to-end encryption.
        <br />
        <img
          className="mt-1 inline-block"
          src={require('../../../assets/images/payout_page/Encryption.svg')}
        />
      </div>
    </div>
  )
}
